<template>
  <header
    :style="inlineStyle"
    id="funnel-header"
    class="funnel-header mobile-layout-one w-full"
    :class="{
      'has-mobile-logo': header.logoMobile,
      [stickyTransparentClass]: true,
      [`layout-${header.layout}`]: true,
      [`m-${header.layoutMobile}`]: true,
      ...stickyTransparentClasses,
    }"
  >
    <div
      :class="{
        [header.width]: true,
      }"
      class="inner"
    >
      <BaseAction
        class="logo-link"
        v-slot="{actionAttrs, handleClick}"
        v-bind="header.logoAction"
        :renderLink="true || header.logoAction.type ? true : false"
      >
        <div>
          <BaseImage
            v-if="header.logoTransparent && isTransparent"
            class="logo logo-transparent"
            :properties="{src: header.logoTransparent}"
          />

          <BaseImage
            v-if="header.logo"
            class="logo"
            :properties="{src: header.logo}"
          />

          <div
            class="logo"
            v-else-if="header.textLogo"
            :class="{'hide-mobile': header.logoMobile}"
          >
            {{ header.textLogo }}
          </div>
        </div>
      </BaseAction>

      <template v-if="header.items.length">
        <Nav class="hide-mobile" :items="header.items" />
        <i
          class="c-icon icon-bars-regular hide-desktop bars"
          @click="$emit('open-side-panel')"
        />
      </template>
      <template v-else>
        <div />
      </template>

      <div class="buttons">
        <BaseAction
          v-if="buttonIsRendered(header.buttonSecondary)"
          v-slot="{actionAttrs, handleClick}"
          v-bind="header.buttonSecondary && header.buttonSecondary.action"
        >
          <BaseButton
            v-bind="actionAttrs"
            class="button-secondary"
            :class="{
              ...buttonVisibilityClasses(header.buttonSecondary),
              'print-wishlist-count':
                header.buttonSecondary.action.showPrintWishlistCount,
            }"
            tagName="a"
            @click.native="handleClick"
            :properties="{
              ...header.buttonSecondary,
              text:
                header.buttonSecondary.text +
                (inCartProductIds.length &&
                header.buttonSecondary.action.showPrintWishlistCount
                  ? ` (${inCartProductIds.length})`
                  : ''),
            }"
          />
        </BaseAction>
        <BaseAction
          v-if="buttonIsRendered(header.buttonPrimary)"
          v-slot="{actionAttrs, handleClick}"
          v-bind="header.buttonPrimary && header.buttonPrimary.action"
        >
          <BaseButton
            v-bind="actionAttrs"
            class="button-primary"
            :class="{
              ...buttonVisibilityClasses(header.buttonPrimary),
              'print-wishlist-count':
                header.buttonPrimary.action.showPrintWishlistCount,
            }"
            tagName="a"
            @click.native="handleClick"
            :properties="{
              ...header.buttonPrimary,
              text:
                header.buttonPrimary.text +
                (inCartProductIds.length &&
                header.buttonPrimary.action.showPrintWishlistCount
                  ? ` (${inCartProductIds.length})`
                  : ''),
            }"
          />
        </BaseAction>
      </div>
    </div>

    <div v-html="inlineScript"></div>
  </header>
</template>

<script>
/* Todo
  add Styling settings
  - Add styling optins
  - Add mobile menu
  - Extend mobile order options, only option is now: mobile-layout-one
*/

import Nav from './Nav.vue';
import BaseButton from '../BaseButton/BaseButton';
import debounce from 'lodash/debounce';
import localState from '../CInspiration/mixin.js';

export default {
  mixins: [localState], // used for previewer, header doesn't get hydrated..
  inject: ['funnelInfo'],
  components: {Nav, BaseButton},
  data: () => ({
    sidePanelOpen: false,
    inlineStyle: {},
    resizeObserver: false,
    inlineScript: '',
    fixTransparentNotStickyEditor: () => {},
  }),
  props: {
    header: {
      type: Object,
      default: () => ({}),
    },
  },
  serverPrefetch() {
    this.inlineScript = `<script>
      const a = window.document.getElementById('funnel-header');
      if(a.classList.contains('transparent')) {
        a.style.marginBottom = a.offsetHeight * -1 + 'px';
      }
    <\/script>`;
  },
  created() {
    this.fixTransparentNotStickyEditor = debounce((event) => {
      if (this.isTransparent) {
        const height = this.$el.offsetHeight;
        this.inlineStyle = {
          'margin-bottom': -height + 'px',
          position: this.isSticky ? 'sticky' : 'relative',
        };
      } else {
        this.inlineStyle = {};
      }
    }, 1500);
  },
  methods: {
    toggleSidePanel() {
      this.sidePanelOpen = !this.sidePanelOpen;
    },
    buttonIsRendered(buttonOptions) {
      const cascading = cascadingValues(buttonOptions.enabled);
      if (
        cascading.mobile === false &&
        cascading.tablet === false &&
        cascading.desktop === false
      ) {
        return false;
      } else {
        return true;
      }
    },
    buttonVisibilityClasses(buttonOptions) {
      const cascading = cascadingValues(buttonOptions.enabled);

      return {
        'hide-mobile': cascading.mobile === false,
        'hide-tablet': cascading.tablet === false,
        'hide-desktop': cascading.desktop === false,
      };
    },

    createResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          this.fixTransparentNotStickyEditor();
        }
      });
      this.resizeObserver.observe(this.$el);
    },
    destroyResizeObserver() {
      if (this.resizeObser) {
        this.resizeObserver.unobserve(this.$el);
        this.resizeObserver = false;
      }
    },
  },
  watch: {
    isSticky() {
      this.fixTransparentNotStickyEditor();
    },
    isTransparent() {
      this.fixTransparentNotStickyEditor();
    },
  },
  // Only triggers (and should) in editor, we've got nasty position absolute isues here related to the virtualization where the header is in
  mounted() {
    this.createResizeObserver();
  },
  beforeDestroy() {
    this.destroyResizeObserver();
  },
  computed: {
    isSticky() {
      return this.header.sticky;
    },

    // Returns 'standard' or 'transparent' unless overwritten for the specific page
    isTransparent() {
      const {pageId} = this.funnelInfo;
      const defaultHeader = this.header.transparent.defaultHeader;
      const isTransparent =
        this.header.transparent.transparentPageIds.includes(pageId);
      const isStandard =
        this.header.transparent.standardPageIds.includes(pageId);

      if (defaultHeader === 'transparent') {
        return isStandard ? false : true;
      } else {
        return isTransparent ? true : false;
      }
    },

    stickyTransparentClasses() {
      const isTransparent = this.isTransparent;
      const isSticky = this.isSticky;

      return {
        sticky: isSticky,
        transparent: isTransparent,
      };
    },

    stickyTransparentClass() {
      const isTransparent = this.isTransparent;
      const isSticky = this.header.sticky;

      /* Not Sticky - not transparent */
      if (!isSticky && !isTransparent) {
        return 'st-none';
      }

      /* Sticky - not transparent */
      if (isSticky && !isTransparent) {
        return 'st-sticky';
      }

      /* Not Sticky - transparent */
      if (!isSticky && isTransparent) {
        return 'st-transparent';
      }

      /* Sticky - transparent */
      if (isSticky && isTransparent) {
        return 'st-sticky-transparent';
      }
    },
  },
};

function cascadingValues(responsiveObj) {
  let desktop = responsiveObj.desktop;
  let tablet =
    typeof responsiveObj.tablet !== 'undefined'
      ? responsiveObj.tablet
      : desktop;
  let mobile =
    typeof responsiveObj.mobile !== 'undefined' ? responsiveObj.mobile : tablet;

  return {
    desktop,
    tablet,
    mobile,
  };
}
</script>

<style lang="scss">
.logo-link {
  position: relative;
}

@media (min-width: 769px) {
  .hide-desktop {
    display: none !important;
  }
}

@media (max-width: 769px) {
  .hide-mobile {
    display: none !important;
  }
}

@media (max-width: 769px) {
  .hide-tablet {
    display: none !important;
  }
}

.funnel-header {
  --logo-width: 100px;
  // --spacing-between: 16px;
  --spacing-between: 1em;
  padding: 0px 20px;
  z-index: 5;
  transition: background 200ms;

  button,
  .logo,
  .bars,
  .navigation {
    transition: 200ms;
  }

  // Should be in render theme function
  .baseButton .c-icon {
    margin-left: 0.5em;
  }

  .baseButton .text {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  // If transparent logo is present hide default logo, till scroll
  .logo-transparent + .logo {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
  }

  &.scrolled.sticky {
    .logo-transparent {
      opacity: 0;
    }

    .logo-transparent + .logo {
      opacity: 1;
    }
  }

  .inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: auto;
    position: relative;
  }

  .container {
    .inner {
      max-width: 1200px;
    }
  }

  .button-secondary {
    margin-right: var(--spacing-between);
  }

  .logo {
    margin-right: 10px;
    font-size: 25px;
    font-weight: 600;
  }

  @media (max-width: 576px) {
    .logo {
      font-size: 18px;
    }
  }

  .bars {
    color: rgba(0, 0, 0, 0.603);
    font-size: 18px;
  }

  img.logo {
    max-width: var(--logo-width);
  }

  @media (min-width: 769px) {
    &.layout-nav-right {
      .navigation {
        margin-left: auto;
        margin-right: var(--spacing-between);
      }
    }

    &.layout-nav-left {
      .navigation {
        margin-right: auto;
      }
    }

    &.layout-logo-center {
      .navigation {
        order: 0;
      }
      .logo-link {
        order: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      .buttons {
        order: 2;
      }
    }
  }

  @media (max-width: 769px) {
    &.m-logo-left-nav-right {
      .logo-link {
        order: 0;
      }
      .buttons {
        order: 1;
        margin-left: auto;
        margin-right: var(--spacing-between);
      }
      .bars {
        order: 2;
      }
    }

    &.m-logo-left-nav-left {
      .bars {
        order: 0;
        margin-right: var(--spacing-between);
      }

      .logo-link {
        order: 1;
      }

      .buttons {
        order: 2;
        margin-left: auto;
      }
    }

    &.m-logo-center-nav-right {
      .buttons {
        order: 0;
      }
      .logo-link {
        order: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      .bars {
        order: 2;
      }
    }

    &.m-logo-right-nav-right {
      .buttons {
        order: 0;
      }

      .logo-link {
        order: 1;
        margin-left: auto;
      }

      .bars {
        order: 2;
      }
    }
    &.m-logo-center-nav-left {
      .bars {
        order: 0;
      }

      .logo-link {
        order: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .buttons {
        order: 2;
      }
    }
    &.m-logo-right-nav-left {
      .bars {
        order: 0;
      }
      .buttons {
        order: 1;
        margin-right: auto;
        margin-left: var(--spacing-between);
      }
      .logo-link {
        order: 2;
      }
    }
  }
}

.w-full {
  width: 100%;
}

/* Not Sticky - not transparent */
.st-none {
  position: relative;
}

/* Sticky - not transparent */
.st-sticky {
  position: sticky;
}

/* Not Sticky - transparent */
.st-transparent {
  position: absolute;
}

/* Sticky - transparent */
.st-sticky-transparent {
  position: sticky;
}
</style>
